import React from 'react';
import { CaretRight } from '@phosphor-icons/react';
import { Text } from '@chatsdet/ui';
import { motion, AnimatePresence } from 'framer-motion';
import type { WorkflowRunStepMetadataDto, ApiSequenceStepResultDto } from '../hooks/useWorkflowRun';
import { ApiSequenceResultDrawer } from '../../../components/ApiSequenceResultDrawer';

type NodeStep = {
  stepId: string;
  method: string;
  urlPattern: string | null;
};

type StepApiCallsProps = {
  metadataJson: WorkflowRunStepMetadataDto | null;
  nodeSteps: NodeStep[];
  workflowId: string | null;
  runId: string;
  runStepId: string;
};

const methodColors: Record<string, string> = {
  GET: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  POST: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  PATCH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

function statusCodeColor(code: number | undefined): string {
  if (!code) return 'text-muted-foreground';
  if (code >= 200 && code < 300) return 'text-green-600 dark:text-green-400';
  if (code >= 400) return 'text-red-600 dark:text-red-400';
  return 'text-amber-600 dark:text-amber-400';
}

function truncateUrl(url: string, maxLength = 45): string {
  if (url.length <= maxLength) return url;
  // Remove protocol prefix for display
  const withoutProtocol = url.replace(/^https?:\/\//, '');
  if (withoutProtocol.length <= maxLength) return withoutProtocol;
  return withoutProtocol.slice(0, maxLength - 1) + '\u2026';
}

function ApiCallRow({
  result,
  nodeStep,
  index,
  onClick,
}: {
  result: ApiSequenceStepResultDto;
  nodeStep: NodeStep | undefined;
  index: number;
  onClick: () => void;
}) {
  // Prefer resolved data from the result, fall back to node config
  const method = (result.method ?? nodeStep?.method ?? 'GET').toUpperCase();
  const url = result.resolvedUrl ?? nodeStep?.urlPattern ?? result.stepId;
  const methodClass = methodColors[method] ?? 'bg-muted text-muted-foreground';

  return (
    <div>
      <motion.button
        type="button"
        onClick={onClick}
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.15, delay: index * 0.04 }}
        className="w-full flex items-center gap-2 py-1 hover:bg-muted/40 rounded px-1 -mx-1 transition-colors cursor-pointer"
      >
        {/* Method badge */}
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${methodClass} shrink-0 font-mono`}
        >
          {method}
        </span>

        {/* URL */}
        <Text size="xs" className="text-muted-foreground truncate min-w-0 font-mono" title={url}>
          {truncateUrl(url)}
        </Text>

        {/* Spacer */}
        <span className="flex-1" />

        {/* Status code */}
        {result.statusCode !== null && result.statusCode !== undefined && (
          <Text size="xs" className={`${statusCodeColor(result.statusCode)} font-mono shrink-0`}>
            {result.statusCode}
          </Text>
        )}

        {/* Error indicator (no status code) */}
        {(result.statusCode === null || result.statusCode === undefined) && result.error && (
          <Text size="xs" className="text-red-600 dark:text-red-400 shrink-0">
            err
          </Text>
        )}

        {/* Duration */}
        {result.durationMs !== null && result.durationMs !== undefined && (
          <Text
            size="xs"
            className="text-muted-foreground w-14 text-right shrink-0 font-mono tabular-nums"
          >
            {result.durationMs}ms
          </Text>
        )}
      </motion.button>

      {/* Inline error preview for failed calls */}
      {!result.success && result.error && (
        <Text size="xs" className="text-destructive truncate ml-7 -mt-0.5 mb-0.5">
          {result.error}
        </Text>
      )}
    </div>
  );
}

/**
 * Expandable list of API calls for an api_sequence workflow step.
 * Follows the same expandable pattern as StepThoughts.
 */
export function StepApiCalls({
  metadataJson,
  nodeSteps,
  workflowId,
  runId,
  runStepId,
}: StepApiCallsProps) {
  const [expanded, setExpanded] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [focusIndex, setFocusIndex] = React.useState<number | undefined>(undefined);

  const stepResults = metadataJson?.stepResults;
  if (!stepResults || stepResults.length === 0) {
    return null;
  }

  // Build a lookup from stepId -> nodeStep for correlation
  const nodeStepMap = new Map(nodeSteps.map((s) => [s.stepId, s]));

  const handleRowClick = (idx: number) => {
    setFocusIndex(idx);
    setDrawerOpen(true);
  };

  return (
    <>
      <div className="ml-4 mt-1.5">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors py-1 px-1.5 -ml-1.5 rounded-md hover:bg-muted/50 group"
        >
          <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
            <CaretRight size={11} className="text-muted-foreground group-hover:text-foreground" />
          </motion.div>
          <Text size="xs" className="text-muted-foreground group-hover:text-foreground">
            {stepResults.length} API call{stepResults.length !== 1 ? 's' : ''}
          </Text>
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="mt-1 ml-1 space-y-0 border-l border-border/50 pl-3">
                {stepResults.map((result, idx) => (
                  <ApiCallRow
                    key={result.stepId}
                    result={result}
                    nodeStep={nodeStepMap.get(result.stepId)}
                    index={idx}
                    onClick={() => handleRowClick(idx)}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ApiSequenceResultDrawer
        isOpen={drawerOpen}
        onOpenChange={setDrawerOpen}
        stepResults={stepResults}
        focusIndex={focusIndex}
        fullBodySource={
          workflowId
            ? { workflowId, runId, runStepId }
            : undefined
        }
      />
    </>
  );
}
